import { randomUuid } from './messengerV2Utils.js';
import { authHeader, fetch_request } from '../../../service/request.js';
import { getBrowserInstallationId } from '../../../service/browserInstallation.js';

const DEFAULT_JSON_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

const DEVICE_IDENTITY_CACHE_TTL_MS = 5 * 60 * 1000;
let deviceIdentityCache = null;
let deviceIdentityInFlight = null;
let deviceIdentityInFlightAuthKey = null;

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const toQueryValue = (value) => {
  const normalized = toStringOrNull(value);
  return normalized ? normalized : null;
};

const buildRequestHeaders = (headers = {}, token = null) => {
  const normalizedHeaders = { ...(headers || {}) };
  const explicitToken = toStringOrNull(token);
  const authorization =
    explicitToken &&
    (explicitToken.startsWith('Bearer ') || explicitToken.startsWith('Token ')
      ? explicitToken
      : `Bearer ${explicitToken}`);
  if (authorization) {
    normalizedHeaders.Authorization = authorization;
  }
  return normalizedHeaders;
};

const buildUrl = (path, query = null) => {
  const normalizedPath = toStringOrNull(path);
  if (!normalizedPath) {
    throw new Error('path is required');
  }
  if (!query) {
    return normalizedPath;
  }
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    const normalized = toQueryValue(value);
    if (normalized != null) {
      search.set(key, normalized);
    }
  });
  const searchString = search.toString();
  return searchString ? `${normalizedPath}?${searchString}` : normalizedPath;
};

const parsePayload = async (response) => {
  try {
    return await response.json();
  } catch {
    try {
      return await response.text();
    } catch {
      return null;
    }
  }
};

const fetchMessengerV2Response = async (path, init = {}, fallbackMessage = null) => {
  const { token = null, ...requestInit } = init || {};
  const normalizedInit = {
    ...requestInit,
    cache: requestInit.cache ?? 'no-store',
    headers: buildRequestHeaders(
      {
        ...DEFAULT_JSON_HEADERS,
        ...(requestInit.headers || {}),
      },
      token,
    ),
  };

  const response = await fetch_request(path, normalizedInit);

  if (!response.ok) {
    throw await buildError(response, fallbackMessage);
  }

  return response;
};

export const resolveMessengerV2ApiErrorMessage = (payload, fallbackMessage = null) => {
  if (payload && typeof payload === 'object') {
    const nestedError = payload.error;
    if (nestedError && typeof nestedError === 'object') {
      const nestedMessage = toStringOrNull(nestedError.message);
      if (nestedMessage) return nestedMessage;
    }
    const directError = typeof nestedError === 'string' ? toStringOrNull(nestedError) : null;
    if (directError) return directError;
    const directMessage = toStringOrNull(payload.message);
    if (directMessage) return directMessage;
  }
  if (typeof payload === 'string') {
    const payloadMessage = toStringOrNull(payload);
    if (payloadMessage) return payloadMessage;
  }
  return toStringOrNull(fallbackMessage);
};

const buildError = async (response, fallbackMessage = null) => {
  const payload = await parsePayload(response);
  const message =
    resolveMessengerV2ApiErrorMessage(payload, fallbackMessage) || `HTTP ${response.status}`;
  const error = new Error(message);
  error.status = response.status;
  if (payload && typeof payload === 'object') {
    error.payload = payload;
    const code = payload.error?.code || payload.code || null;
    if (code) {
      error.code = code;
    }
  } else {
    error.payload = payload;
    if (typeof payload === 'string') {
      error.responseText = payload;
    }
  }
  return error;
};

const assertMlsEnrollmentAvailable = (payload) => {
  const enrollment = payload?.enrollment;
  if (!enrollment || enrollment.status !== 'unavailable') return payload;

  const error = new Error('MLS device enrollment worker is unavailable');
  error.code = 'enrollment_worker_unavailable';
  error.reason = enrollment.reason || null;
  error.payload = payload;
  throw error;
};

export const requestMessengerV2Json = async (path, init = {}, fallbackMessage = null) => {
  const response = await fetchMessengerV2Response(path, init, fallbackMessage);
  return parsePayload(response);
};

export const createMessengerV2MultipartAttachmentUpload = ({
  filename,
  contentType = 'application/octet-stream',
  byteSize,
} = {}) =>
  requestMessengerV2Json(
    '/e2ee/v1/attachments/multipart',
    {
      method: 'POST',
      body: JSON.stringify({
        filename,
        content_type: contentType,
        byte_size: byteSize,
      }),
    },
    'Could not start attachment upload',
  );

export const refreshMessengerV2MultipartAttachmentUpload = ({
  objectKey,
  uploadId,
  partNumbers = [],
} = {}) =>
  requestMessengerV2Json(
    '/e2ee/v1/attachments/multipart/refresh',
    {
      method: 'POST',
      body: JSON.stringify({
        object_key: objectKey,
        upload_id: uploadId,
        part_numbers: partNumbers,
      }),
    },
    'Could not resume attachment upload',
  );

export const completeMessengerV2MultipartAttachmentUpload = ({
  objectKey,
  uploadId,
  expectedParts,
} = {}) =>
  requestMessengerV2Json(
    '/e2ee/v1/attachments/multipart/complete',
    {
      method: 'POST',
      body: JSON.stringify({
        object_key: objectKey,
        upload_id: uploadId,
        expected_parts: expectedParts,
      }),
    },
    'Could not complete attachment upload',
  );

const requestJson = requestMessengerV2Json;

const resolveDeviceIdentityAuthKey = async () => {
  const installationId = await getBrowserInstallationId();
  return `${authHeader()?.Authorization || 'anonymous'}:${installationId}`;
};

export const fetchConversationMlsGroup = async ({ conversationId, clientId, reissue } = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId) {
    throw new Error('conversationId is required to fetch MLS group');
  }

  const query = {};
  if (clientId != null) {
    query.client_id = clientId;
  }
  if (reissue === true) {
    query.reissue = 'true';
  }

  return requestJson(
    buildUrl(
      `/api/v1/conversations/${encodeURIComponent(normalizedConversationId)}/mls-group`,
      query,
    ),
    {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    },
    'Failed to fetch MLS group',
  );
};

export const fetchPendingMlsWelcomes = async ({ clientId } = {}) => {
  const normalizedClientId = toStringOrNull(clientId);
  const query = normalizedClientId ? { client_id: normalizedClientId } : {};

  const response = await fetchMessengerV2Response(
    buildUrl('/api/v1/mls/welcomes', query),
    {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        ...(normalizedClientId ? { 'X-MLS-Client-Id': normalizedClientId } : {}),
      },
    },
    'Failed to fetch pending MLS welcomes',
  );

  const payload = await parsePayload(response);
  if (Array.isArray(payload)) {
    return { items: payload, next_cursor: null };
  }
  return payload && Array.isArray(payload.items) ? payload : { items: [], next_cursor: null };
};

export const fetchLatestMlsKeyPackage = async ({ clientId, ciphersuite } = {}) => {
  const normalizedClientId = toStringOrNull(clientId);
  if (!normalizedClientId) {
    throw new Error('clientId is required');
  }

  const query = {
    client_id: normalizedClientId,
  };
  if (ciphersuite != null) {
    query.ciphersuite = ciphersuite;
  }

  const response = await fetchMessengerV2Response(
    buildUrl('/api/v1/mls/key_packages/latest', query),
    {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    },
  );

  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw await buildError(response, 'Failed to fetch latest MLS key package');
  }

  const payload = await parsePayload(response);
  return payload;
};

export const getMlsDeviceIdentity = async ({ force = false } = {}) => {
  const authKey = await resolveDeviceIdentityAuthKey();
  const now = Date.now();
  if (
    force !== true &&
    deviceIdentityCache &&
    deviceIdentityCache.authKey === authKey &&
    deviceIdentityCache.expiresAt > now
  ) {
    return deviceIdentityCache.payload;
  }

  if (force !== true && deviceIdentityInFlight && deviceIdentityInFlightAuthKey === authKey) {
    return deviceIdentityInFlight;
  }

  deviceIdentityInFlightAuthKey = authKey;
  deviceIdentityInFlight = requestJson(
    '/api/v1/mls/device_identity',
    { cache: 'no-store' },
    'Failed to fetch MLS device identity',
  )
    .then((payload) => {
      deviceIdentityCache = {
        authKey,
        payload,
        expiresAt: Date.now() + DEVICE_IDENTITY_CACHE_TTL_MS,
      };
      return payload;
    })
    .finally(() => {
      deviceIdentityInFlight = null;
      deviceIdentityInFlightAuthKey = null;
    });

  return deviceIdentityInFlight;
};

export const getJoinBundle = async ({
  groupId,
  identityId,
  keyPackageB64,
  token,
  reissue = false,
} = {}) => {
  const normalizedGroupId = toStringOrNull(groupId);
  if (!normalizedGroupId) {
    throw new Error('groupId is required');
  }

  const response = await fetchMessengerV2Response(
    `/api/v1/mls/groups/${encodeURIComponent(normalizedGroupId)}/join_bundle`,
    {
      method: 'POST',
      token,
      body: JSON.stringify({
        identity_id: identityId ?? null,
        client_id: identityId ?? null,
        key_package_b64: keyPackageB64 ?? null,
        reissue: Boolean(reissue),
      }),
    },
    'Failed to fetch MLS join bundle',
  );

  if (response.status === 404) {
    return null;
  }

  return parsePayload(response);
};

export const publishMlsKeyPackage = async ({
  identityId,
  clientId,
  ciphersuite,
  keyPackageB64,
  force = false,
  enrollmentMode = null,
} = {}) => {
  const response = await fetchMessengerV2Response(
    `/api/v1/mls/key_packages${force ? '?force=true' : ''}`,
    {
      method: 'POST',
      body: JSON.stringify({
        key_package: {
          ...(identityId ? { identity_id: identityId } : {}),
          ...(clientId ? { client_id: clientId } : {}),
          ...(ciphersuite != null ? { ciphersuite } : {}),
          ...(enrollmentMode ? { enrollment_mode: enrollmentMode } : {}),
          key_package_b64: keyPackageB64,
        },
      }),
    },
  );

  const payload = await parsePayload(response);
  if (response.status === 409) {
    return { status: 409, ok: true, alreadyExists: true };
  }
  if (response.status === 401) {
    const error = new Error((payload && payload.message) || 'Unauthorized');
    error.status = 401;
    if (payload) {
      error.payload = payload;
    }
    throw error;
  }
  if (!response.ok) {
    const error = new Error(
      (payload && payload.message) || `Failed to publish MLS key package (${response.status})`,
    );
    error.status = response.status;
    if (payload) {
      error.payload = payload;
    }
    throw error;
  }
  return assertMlsEnrollmentAvailable(payload);
};

export const reissueMlsWelcomes = async ({
  groupId,
  identityId,
  keyPackageB64,
  replaceWelcomeId = null,
  token,
} = {}) => {
  const normalizedGroupId = toStringOrNull(groupId);
  if (!normalizedGroupId) {
    throw new Error('groupId is required to reissue MLS welcomes');
  }
  if (!identityId) {
    throw new Error('identityId is required to reissue MLS welcomes');
  }

  const response = await fetchMessengerV2Response(
    `/api/v1/mls/groups/${encodeURIComponent(normalizedGroupId)}/reissue_welcomes`,
    {
      method: 'POST',
      token,
      body: JSON.stringify({
        identity_id: identityId,
        key_package_b64: keyPackageB64,
        ...(replaceWelcomeId ? { replace_welcome_id: replaceWelcomeId } : {}),
      }),
    },
    'Failed to reissue MLS welcomes',
  );

  if (response.status === 404) {
    return null;
  }

  return parsePayload(response);
};

export const acknowledgeMlsWelcomes = async ({ groupId, welcomeIds, clientId } = {}) => {
  const normalizedGroupId = toStringOrNull(groupId);
  if (!normalizedGroupId) {
    throw new Error('groupId is required to acknowledge welcomes');
  }
  if (!Array.isArray(welcomeIds)) {
    throw new Error('welcomeIds must be an array');
  }

  const normalizedIds = Array.from(
    new Set(welcomeIds.map((value) => toStringOrNull(value)).filter(Boolean)),
  );

  if (normalizedIds.length === 0) {
    return true;
  }

  const response = await fetchMessengerV2Response(
    `/api/v1/mls/groups/${encodeURIComponent(normalizedGroupId)}/welcomes/ack`,
    {
      method: 'POST',
      body: JSON.stringify({
        welcome_ids: normalizedIds,
        ...(clientId ? { client_id: clientId } : {}),
      }),
    },
    'Failed to acknowledge MLS welcomes',
  );

  if (response.status === 404) {
    return true;
  }

  const payload = await parsePayload(response);
  return payload ?? { acked: normalizedIds };
};

export const fetchMessengerV2CallTargetClient = async ({
  groupId,
  targetUserId,
  conversationId,
} = {}) => {
  const normalizedGroupId = toStringOrNull(groupId);
  const normalizedTargetUserId = toStringOrNull(targetUserId);
  if (!normalizedGroupId) {
    throw new Error('groupId is required to resolve call target');
  }

  const response = await fetchMessengerV2Response(
    buildUrl(`/api/v1/mls/groups/${encodeURIComponent(normalizedGroupId)}/call_target`, {
      target_user_id: normalizedTargetUserId,
      conversation_id: conversationId,
    }),
    {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    },
    'Failed to resolve call target',
  ).catch((error) => {
    if (error?.status === 404) return null;
    throw error;
  });

  if (!response) return null;

  return parsePayload(response);
};

export const clearMessengerV2Conversation = async ({ conversationId } = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId) {
    throw new Error('conversationId is required');
  }

  return requestJson(
    `/api/conversations/${encodeURIComponent(normalizedConversationId)}/clear`,
    { method: 'POST' },
    'Failed to clear chat',
  );
};

export const setMessengerV2ConversationPinned = async ({ conversationId, pinned } = {}) => {
  const id = toStringOrNull(conversationId);
  if (!id || typeof pinned !== 'boolean') throw new Error('conversationId and pinned are required');
  return requestJson(
    `/api/conversations/${encodeURIComponent(id)}/pin`,
    { method: 'PATCH', body: JSON.stringify({ pinned }) },
    'Failed to update chat pin',
  );
};

export const fetchMessengerV2InboxActivity = async () =>
  requestJson(
    '/api/conversations/inbox_activity',
    { method: 'GET' },
    'Failed to load message activity',
  );

export const acknowledgeMessengerV2InboxActivity = async ({ throughActivityVersion = null } = {}) =>
  requestJson(
    '/api/conversations/inbox_activity/acknowledge',
    {
      method: 'POST',
      body: JSON.stringify({ through_activity_version: throughActivityVersion }),
    },
    'Failed to acknowledge message activity',
  );

export const deleteMessengerV2DirectConversation = async ({ conversationId } = {}) => {
  const normalizedConversationId = toStringOrNull(conversationId);
  if (!normalizedConversationId) {
    throw new Error('conversationId is required');
  }

  return requestJson(
    `/api/conversations/${encodeURIComponent(normalizedConversationId)}`,
    { method: 'DELETE' },
    'Failed to delete chat',
  );
};

export const blockMessengerV2User = async ({ userId } = {}) => {
  const normalizedUserId = toStringOrNull(userId);
  if (!normalizedUserId) {
    throw new Error('userId is required');
  }

  return requestJson(
    '/api/blocks',
    {
      method: 'POST',
      body: JSON.stringify({ blocked_user_id: normalizedUserId }),
    },
    'Failed to block user',
  );
};

export const unblockMessengerV2User = async ({ userId, blockId = null } = {}) => {
  const normalizedUserId = toStringOrNull(userId);
  const normalizedBlockId = toStringOrNull(blockId);
  if (!normalizedUserId && !normalizedBlockId) {
    throw new Error('userId or blockId is required');
  }

  const path = normalizedBlockId
    ? `/api/blocks/${encodeURIComponent(normalizedBlockId)}`
    : buildUrl('/api/blocks', { blocked_user_id: normalizedUserId });
  return requestJson(path, { method: 'DELETE' }, 'Failed to unblock user').catch((error) => {
    if (error?.status === 404) return true;
    throw error;
  });
};

export const sendMlsMessage = async ({
  groupId,
  senderClientId,
  identityId,
  msgType = 'application',
  epoch,
  ciphersuite,
  envelopeB64,
  ciphertextB64,
  contentType,
  clientMsgId,
  welcomes,
  historyCiphertextB64,
  historyIvB64,
  historyAlg,
  notificationCiphertextB64,
  notificationIvB64,
  notificationAlg,
} = {}) => {
  const normalizedGroupId = toStringOrNull(groupId);
  if (!normalizedGroupId) throw new Error('groupId is required');
  if (!senderClientId) throw new Error('senderClientId is required');
  const encodedEnvelope = envelopeB64 || ciphertextB64;
  if (!encodedEnvelope) throw new Error('envelopeB64 is required');

  const messageClientId = clientMsgId || randomUuid();
  const normalizedMsgType = msgType || 'application';
  const normalizedContentType =
    contentType ?? (normalizedMsgType === 'application' ? 'application/mls' : null);

  const response = await fetchMessengerV2Response(
    `/api/v1/mls/groups/${encodeURIComponent(normalizedGroupId)}/messages`,
    {
      method: 'POST',
      headers: {
        'X-MLS-Group-Id': String(normalizedGroupId),
        ...(identityId ? { 'X-MLS-Client-Id': String(identityId) } : {}),
        ...(ciphersuite != null ? { 'X-MLS-Ciphersuite': String(ciphersuite) } : {}),
        ...(epoch != null ? { 'X-MLS-Epoch': String(epoch) } : {}),
      },
      body: JSON.stringify({
        msg_type: normalizedMsgType,
        ciphertext_b64: ciphertextB64 ?? encodedEnvelope,
        envelope_b64: encodedEnvelope,
        client_msg_id: messageClientId,
        sender_client_id: senderClientId,
        content_type: normalizedContentType,
        history_ciphertext_b64: historyCiphertextB64 ?? null,
        history_iv_b64: historyIvB64 ?? null,
        history_alg: historyAlg ?? null,
        notification_ciphertext_b64: notificationCiphertextB64 ?? null,
        notification_iv_b64: notificationIvB64 ?? null,
        notification_alg: notificationAlg ?? null,
        welcomes: welcomes ?? null,
      }),
    },
    'Failed to send MLS message',
  );

  const result = await parsePayload(response);
  const normalized = result && typeof result === 'object' ? { ...result } : {};
  if (!('client_msg_id' in normalized) || normalized.client_msg_id == null) {
    normalized.client_msg_id = messageClientId;
  }
  const serverSeq = normalized.server_seq ?? result?.server_seq ?? null;
  normalized.server_seq = serverSeq;
  return normalized;
};

export const fetchMlsMessages = async ({
  groupId,
  since,
  clientId,
  handshakeAfterEpoch = null,
} = {}) => {
  const normalizedGroupId = toStringOrNull(groupId);
  if (!normalizedGroupId) throw new Error('groupId is required');

  const params = new URLSearchParams();
  if (since != null && since !== '') {
    if (typeof since === 'object' && !Array.isArray(since)) {
      Object.entries(since).forEach(([cursorGroupId, cursorSeq]) => {
        if (cursorGroupId && cursorSeq != null && cursorSeq !== '') {
          params.set(`since[${cursorGroupId}]`, String(cursorSeq));
        }
      });
    } else {
      params.set(`since[${normalizedGroupId}]`, String(since));
    }
  }
  if (handshakeAfterEpoch != null && handshakeAfterEpoch !== '') {
    params.set('handshake_after_epoch', String(handshakeAfterEpoch));
  }
  const query = params.toString();
  const headers = {};
  if (clientId) {
    headers['X-MLS-Client-Id'] = String(clientId);
  }

  const response = await fetchMessengerV2Response(
    `/api/v1/mls/groups/${encodeURIComponent(normalizedGroupId)}/pending${query ? `?${query}` : ''}`,
    {
      cache: 'no-store',
      headers,
    },
    'Failed to fetch MLS messages',
  );

  const payload = await parsePayload(response);
  if (Array.isArray(payload)) {
    return { items: payload, next_cursor: null };
  }
  if (payload && Array.isArray(payload.items)) {
    return payload;
  }
  if (payload && Array.isArray(payload.messages)) {
    return {
      items: payload.messages,
      next_cursor: payload.next_cursor ?? payload.nextCursor ?? null,
    };
  }
  return payload ?? { items: [], next_cursor: null };
};

export const fetchMlsMessageHistory = async ({ groupId, before, limit = 50, clientId } = {}) => {
  const normalizedGroupId = toStringOrNull(groupId);
  if (!normalizedGroupId) throw new Error('groupId is required');

  const params = new URLSearchParams();
  if (before != null && before !== '') {
    params.set('before', String(before));
  }
  if (limit != null && limit !== '') {
    params.set('limit', String(limit));
  }
  const query = params.toString();
  const headers = {};
  if (clientId) {
    headers['X-MLS-Client-Id'] = String(clientId);
  }

  const response = await fetchMessengerV2Response(
    `/api/v1/mls/groups/${encodeURIComponent(normalizedGroupId)}/history${query ? `?${query}` : ''}`,
    {
      cache: 'no-store',
      headers,
    },
    'Failed to fetch MLS message history',
  );

  const payload = await parsePayload(response);
  if (payload && Array.isArray(payload.items)) {
    return payload;
  }
  if (Array.isArray(payload)) {
    return { items: payload, next_before: null, has_more: false };
  }
  return { items: [], next_before: null, has_more: false };
};
